
import BaseActionButton from '@/components/common/BaseActionButton.vue'

export default {
  name: 'FormDialog',
  components: { BaseActionButton },
  props: {
    title: { type: String, default: '' },
    message: { type: String, default: '' },
    /**
     * 字段规格数组。每项：
     *   { key, label, type, placeholder, required, maxlength, min, max, integer, minlength,
     *     rows, options, help, httpsOnly, requiredMessage, validate }
     * type: text（默认）| textarea | number | url | password | select | readonly-secret
     */
    fields: {
      type: Array,
      required: true,
      validator: arr => arr.length > 0 && arr.every(f => f && f.key && f.label)
    },
    okLabel: { type: String, default: '确定' },
    cancelLabel: { type: String, default: '取消' },
    persistent: { type: Boolean, default: false },
    /**
     * C∩D：破坏性 + 必须输入的表单（如「删除订单商品」需填删除原因）。
     * true → 确认键走项目危险色 --destructive、后果块醒目标出。
     * 独立性：只影响配色与后果块，不改变字段/校验/提交链路——不是「万能弹窗」的开口。
     * showForm() 在签名层强制：danger:true 必须同时给 consequence（说不清后果就别标红）。
     */
    danger: { type: Boolean, default: false },
    /** 危险表单的必填后果文案（danger:true 时由 showForm 保证非空） */
    consequence: { type: String, default: '' },
    /** 可选 async (values, api) => payload|void：在弹窗内提交；抛错则保留弹窗显示原因 */
    onSubmit: { type: Function, default: null },
    /** 只读敏感值的取数（仅 type=readonly-secret 用；默认取 value） */
    secretValue: { type: Function, default: null }
  },
  data () {
    return { values: {}, errors: {}, loading: false, error: '' }
  },
  created () {
    // props 在 Dialog.create 时已传入，created 里即可按字段规格初始化（默认值取 f.value）
    this.fields.forEach(f => {
      this.$set(this.values, f.key, f.value == null ? '' : f.value)
    })
  },
  methods: {
    // ↓↓ Quasar 自定义弹窗三件套：插件只认这三个入口
    show () { this.$refs.dlg.show() },
    hide () { this.$refs.dlg.hide() },
    onDialogHide () { this.$emit('hide') },

    onCancel () {
      if (this.loading) return
      this.hide()
    },

    inputType (f) {
      if (f.type === 'password') return 'password' // 密码/验证码一律掩码，不得明文回显
      if (f.type === 'number') return 'number'
      if (f.type === 'url') return 'url'
      return 'text'
    },

    valueOf (f) {
      return typeof this.secretValue === 'function' ? this.secretValue(f) : String(this.values[f.key] || '')
    },

    /**
     * 复制只读敏感值。**不用 import utils/dialog.js 的 showMessage**——那会形成循环依赖
     * （dialog.js 反过来 import 本组件），所以这里直接用 $q.notify 做反馈。
     * 取数走与展示同一条 valueOf()，保证「看到的就是复制的」。
     * 失败回退链：navigator.clipboard（需安全上下文 + 权限）→ execCommand('copy') 兜底；
     * 两者都不行才报「请手动选择」——此时值仍可选中（.df-secret 是 user-select:all）。
     */
    async copySecret (f) {
      const text = this.valueOf(f)
      if (!text) return
      let ok = false
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
          ok = true
        }
      } catch (_) { ok = false }
      if (!ok) ok = this.copyViaTextarea(text)
      this.$q.notify(ok
        ? { type: 'positive', message: '已复制', timeout: 1200 }
        : { type: 'negative', message: '复制失败，请手动选择', timeout: 2500 })
    },

    /** 与 Order.vue 复制单号同法的兜底路径（headless/无权限环境下 clipboard API 会抛） */
    copyViaTextarea (text) {
      try {
        const ta = document.createElement('textarea')
        ta.value = text
        ta.setAttribute('readonly', '')
        ta.style.position = 'fixed'
        ta.style.top = '-1000px'
        document.body.appendChild(ta)
        ta.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(ta)
        return ok
      } catch (_) { return false }
    },

    clearField (key) {
      if (this.errors[key]) this.$delete(this.errors, key)
      if (this.error) this.error = ''
    },

    onEnter () {
      // 单行输入框回车 = 提交（移动端键盘「完成」也走这条）；textarea 回车是换行，不拦
      this.submit()
    },

    validateField (f) {
      const raw = this.values[f.key]
      const s = raw == null ? '' : String(raw)
      if (f.required && s.trim() === '') return f.requiredMessage || `请填写${f.label}`
      if (f.maxlength && s.length > f.maxlength) return `${f.label}最多 ${f.maxlength} 个字符`
      if (f.type === 'password' && f.minlength && s !== '' && s.length < f.minlength) return `${f.label}至少 ${f.minlength} 位`
      if (f.type === 'number' && s.trim() !== '') {
        const n = Number(s)
        if (!Number.isFinite(n)) return `${f.label}必须是数字`
        if (f.integer && !Number.isInteger(n)) return `${f.label}必须是整数`
        if (f.min != null && n < f.min) return `${f.label}不能小于 ${f.min}`
        if (f.max != null && n > f.max) return `${f.label}不能大于 ${f.max}`
      }
      if (f.type === 'url' && s.trim() !== '') {
        if (!/^https?:\/\/.+/i.test(s.trim())) return `${f.label}需为完整网址（http:// 或 https:// 开头）`
        if (f.httpsOnly && !/^https:\/\/.+/i.test(s.trim())) return `${f.label}仅接受 https 链接`
      }
      if (typeof f.validate === 'function') {
        const m = f.validate(raw, { ...this.values })
        if (m) return m
      }
      return ''
    },

    validateAll () {
      const next = {}
      this.fields.forEach(f => {
        const m = this.validateField(f)
        if (m) next[f.key] = m
      })
      this.errors = next
      return Object.keys(next).length === 0
    },

    async submit () {
      if (this.loading) return // 重复点击保护（按钮 disabled 之外再兜一层）
      this.error = ''
      if (!this.validateAll()) return // 校验不过 → 弹窗不关、不发请求
      const payload = { ...this.values }
      if (!this.onSubmit) {
        this.$emit('ok', payload)
        this.hide()
        return
      }
      this.loading = true
      try {
        const out = await this.onSubmit(payload)
        this.$emit('ok', out === undefined ? payload : out)
        this.hide()
      } catch (e) {
        const server = e && e.response && e.response.data && e.response.data.message
        this.error = server || (e && e.message) || '提交失败，请重试'
      } finally {
        this.loading = false
      }
    }
  }
}
